import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import UrlHandlerApiService from "./url-handler.service";

const PriceOverrideApiService = {
  URL: "",
  async init() {
    Vue.use(VueAxios, axios);
    this.URL = await UrlHandlerApiService.getBaseUrl();
  },

  /**
   * List price overrides (all shops or filtered)
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 0)
   * @param {number} params.size - Page size (default: 50)
   * @param {number} [params.shopId] - Optional shop ID filter
   * @param {string} [params.name] - Optional name filter
   * @param {string} [params.sku] - Optional SKU filter
   * @param {string} [params.ean] - Optional EAN filter
   * @returns {Promise}
   */
  list(params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 0,
      size: params.size || 10,
      ...(params.shopId && { shopId: params.shopId }),
      ...(params.name && { name: params.name }),
      ...(params.sku && { sku: params.sku }),
      ...(params.ean && { ean: params.ean })
    });
    return Vue.axios.get(`${this.URL}/api/shop/catalog/override/price?${queryParams}`);
  },

  /**
   * Get single price override by ID
   * @param {number} id - Price override ID
   * @returns {Promise}
   */
  get(id) {
    return Vue.axios.get(`${this.URL}/api/shop/catalog/override/price/${id}`);
  },

  /**
   * Upsert price override
   * Can be used with:
   * - shopId + sku
   * - itemVariationId
   * - itemId (only if item has exactly 1 variation)
   * @param {Object} data - Price override data
   * @param {number} [data.shopId] - Shop ID (required with sku)
   * @param {string} [data.sku] - SKU (required with shopId)
   * @param {number} [data.itemVariationId] - Item variation ID
   * @param {number} [data.itemId] - Item ID (only if item has exactly 1 variation)
   * @param {number} [data.price] - Price
   * @param {number} [data.purchasePrice] - Purchase price
   * @param {number} [data.targetPrice] - Target price
   * @returns {Promise}
   */
  upsert(data) {
    return Vue.axios.post(`${this.URL}/api/shop/catalog/override/price`, data);
  },

  /**
   * Delete price override by ID
   * @param {number} id - Price override ID
   * @returns {Promise}
   */
  delete(id) {
    return Vue.axios.delete(`${this.URL}/api/shop/catalog/override/price/${id}`);
  },

  /**
   * Import price overrides from CSV file
   * @param {number} shopId - Shop ID
   * @param {File} file - CSV file
   * @returns {Promise}
   */
  importCsv(shopId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return Vue.axios.post(
      `${this.URL}/api/shop/catalog/override/price/import?shopId=${shopId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );
  }
};

export default PriceOverrideApiService;
